import type { Emitter, IDisposable, IEvent } from "monaco-editor";

import { isNullish, notNullish } from "@de-xima/xima-common-js-lang";

type TStorageScopeGlobal = 0;
type TStorageScopeWorkspace = 1;

let instance: LocalStorageService | undefined;

/**
 * An `IStorageService` that stores settings and preferences in the browser's
 * local storage. This is used by the monaco editor, for example, to store
 * favorite auto completions etc.
 */
class LocalStorageService {
  onDidChangeStorage: IEvent<string>;
  onDidChangeValue: IEvent<string>;
  onWillSaveState: () => IDisposable;

  private _onDidChangeStorage: Emitter<string>;
  private _onDidChangeValue: Emitter<string>;

  constructor() {
    this._onDidChangeStorage = new monaco.Emitter();
    this._onDidChangeValue = new monaco.Emitter();
    this.onDidChangeStorage = this._onDidChangeStorage.event;
    this.onDidChangeValue = this._onDidChangeValue.event;
    this.onWillSaveState = () => ({ dispose: () => undefined });
  }

  dispose(): void {
    this._onDidChangeStorage.dispose();
    this._onDidChangeValue.dispose();
  }

  /**
   * @param key Key for which to retrieve a value.
   * @param scope Scope where to get the value from.
   * @param fallbackValue Fallback value when the no value is available.
   * @returns The value at the given key.
   */
  get(key: string, _scope: TStorageScopeWorkspace | TStorageScopeGlobal, fallbackValue?: string): string | undefined {
    const value = localStorage.getItem(this._getKey(key));
    return notNullish(value) ? value : fallbackValue;
  }

  /**
   * @param key Key for which to retrieve a value.
   * @param scope Scope where to get the value from.
   * @param fallbackValue Fallback value when the no value is available.
   * @returns The value at the given key.
   */
  getBoolean(
    key: string,
    _scope: TStorageScopeWorkspace | TStorageScopeGlobal,
    fallbackValue?: boolean,
  ): boolean | undefined {
    const value = localStorage.getItem(this._getKey(key));
    return notNullish(value) ? value === "true" : fallbackValue;
  }

  /**
   * @param key Key for which to retrieve a value.
   * @param scope Scope where to get the value from.
   * @param fallbackValue Fallback value when the no value is available.
   * @returns The value at the given key.
   */
  getNumber(
    key: string,
    _scope: TStorageScopeWorkspace | TStorageScopeGlobal,
    fallbackValue?: number,
  ): number | undefined {
    const value = localStorage.getItem(this._getKey(key));
    return notNullish(value) ? Number.parseInt(value, 10) : fallbackValue;
  }

  logStorage(): void {
    // cannot list items in localStorage
  }

  /**
   * @param key Key for which to remove a value.
   * @param scope Scope where to remove a value from.
   */
  remove(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * @param key Key for which to remove a value.
   * @param value Value to store.
   * @param scope Scope where to store the value.
   */
  store(key: string, value: string | boolean | number | undefined | null): void {
    if (isNullish(value)) {
      this.remove(key);
    } else {
      localStorage.setItem(this._getKey(key), String(value));
    }
  }

  /**
   * @param key Key name.
   * @returns Name of the key with the prefix added.
   */
  private _getKey(key: string): string {
    return `monaco.${key}`;
  }
}

/**
 * This needs to be created on-the-fly as the monaco library is required for
 * instantiating the service. It may not be available when this script is first
 * loaded.
 * @returns The singleton instance of the local storage
 * service.
 */
export function getLocalStorageServiceInstance(): LocalStorageService {
  if (instance === undefined) {
    instance = new LocalStorageService();
  }
  return instance;
}
